import React, { Fragment, useEffect, useState } from "react";
import { modal } from "components/HeadlessModal";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { apiGet, apiPut } from "helpers/Request";
import { useAuth } from "hooks/Auth";
import { transform as contentTransformer } from "schemas/Content";
import { transform as authorTransformer } from "schemas/Author";
import { Form, Formik } from "formik";
import TextInput from "components/FormElements/TextInput";
import AutocompleteInput from "components/FormElements/AutocompleteInput";
import TextArea from "components/FormElements/TextArea";
import { toast } from "react-hot-toast";
import { CubeIcon } from "@heroicons/react/outline";

export default function PostUploadCart(props) {
    const [files, setFiles] = useState([]);
    const [open, setOpen] = useState(false);

    useEffect(
        () => {
            if (props?.uploaded.length > 0) {
                setFiles(props.uploaded);
                setOpen(true);
            } else {
                setOpen(false);
            }
        },
        [props.uploaded],
        () => {
            setOpen(false);
            setFiles([]);
        }
    );

    useEffect(() => {
        if (!open) return;

        modal({
            variant: "default",
            title: "Files uploaded successfully",
            children: <FileReviewer files={files} />,
            buttons: () => (
                <Fragment>
                    <SecondaryButton onClick={modal.dismiss}>
                        Skip Review
                    </SecondaryButton>
                </Fragment>
            ),
        });
    }, [open, setOpen, files]);

    return null;
}

async function loadFiles(files, publication) {
    const promises = files
        .filter((file) => file !== undefined)
        .map((file) => {
            return apiGet("/v4/content/" + file, publication).then((resp) =>
                contentTransformer(resp)
            );
        });
    return Promise.all(promises);
}

function FileReviewer(props) {
    const [files, setFiles] = useState([]);
    const [step, setStep] = useState(0);
    const [current, setCurrent] = useState(null);
    const auth = useAuth();

    const nextStep = () => {
        if (step + 1 >= files.length) {
            setFiles([]);
            modal.dismiss();
            return;
        }
        setStep(step + 1);
    };

    useEffect(() => {
        loadFiles(props.files, auth.publication).then((fileList) => {
            setFiles(fileList);
            setStep(0);
            // setCurrent(fileList[0]);
        });
    }, [props.files, auth.publication]);

    useEffect(() => {
        setCurrent(files[step]);
    }, [step, files]);

    if (!current) return null;

    return (
        <div>
            <div className="flex space-x-4">
                <div className="max-w-1/3">
                    <div className="relative h-24 w-24">
                        <div className="absolute inset-0 z-0 w-full py-10 text-center">
                            <CubeIcon className="mx-auto h-10 w-10 animate-rotate-pulse text-gray-300" />
                        </div>
                        <img
                            src={current.attachment.imgix_url}
                            alt={current.attachment.uuid}
                            className="relative z-0 h-24 w-full object-cover"
                        />
                    </div>
                </div>
                <div className="flex-grow">
                    <FileForm
                        current={current}
                        auth={auth}
                        nextStep={nextStep}
                    />
                </div>
            </div>
        </div>
    );
}

function FileForm(props) {
    return (
        <Formik
            enableReinitialize={true}
            initialValues={{
                title: props.current.title,
                content: props.current.content,
                authors: props.current.authors.map((au) => ({
                    label: au.name,
                    value: au.uuid,
                })),
            }}
            onSubmit={(values) => {
                const data = {
                    authors: values.authors.map((author) => author.value),
                    title: values.title,
                    content: values.content,
                    acquire_lock: true,
                };
                modal.update({
                    disabled: true,
                });
                const id = toast.loading("Updating...");
                apiPut(
                    `/v4/content/${props.current.uuid}`,
                    props.auth.publication,
                    data
                ).then(() => {
                    toast.dismiss(id);
                    toast.success("Updated!");
                    modal.update({
                        disabled: false,
                    });
                    props.nextStep();
                });
            }}
        >
            <Form>
                <div className="space-y-4">
                    <TextInput label="Title" id="title" name="title" />
                    <TextArea label="Caption" id="content" name="content" />
                    <AutocompleteInput
                        label="Search authors:"
                        id="authors"
                        name="authors"
                        onRequestSearch={async (term) => {
                            const items = await apiGet(
                                "/v4/search",
                                props.auth.publication,
                                {
                                    keyword: term,
                                    per_page: 5,
                                    type: "author",
                                }
                            );

                            return authorTransformer(items).map((item) => {
                                return {
                                    label: item.name,
                                    value: item.uuid,
                                };
                            });
                        }}
                    />
                </div>
                <div className="flex space-x-2">
                    <SecondaryButton
                        onClick={props.nextStep}
                        className="ml-auto"
                    >
                        Next
                    </SecondaryButton>
                    <PrimaryButton type="submit">Save and Next</PrimaryButton>
                </div>
            </Form>
        </Formik>
    );
}
